import React, { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { ArrowLeft, Save } from "lucide-react";
import {
  createDistrict,
  updateDistrict,
  fetchDistrictById,
  resetDistrictStatuses,
} from "../../store/slices/districtsSlice";
import { fetchStates } from "../../store/slices/statesSlice";
import { TextField, CheckboxField } from "../../components/ui/FormControls";
import { showApiError, showApiSuccess } from "../../utils/toast";
import Loader from "../../components/Loader";

const districtSchema = yup.object({
  state_uuid: yup.string().required("State is required"),
  name: yup
    .string()
    .required("District name is required")
    .max(255, "District name must be less than 255 characters"),
  status: yup.boolean(),
});

export default function AddDistrictPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const editUuid = searchParams.get("edit");

  const {
    district,
    districtCreateStatus,
    districtUpdateStatus,
    districtFetchStatus,
    loading,
  } = useSelector((state) => state.districts);

  const { states = [] } = useSelector((state) => state.states);

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(districtSchema),
    defaultValues: {
      state_uuid: "",
      name: "",
      status: true,
    },
  });

  useEffect(() => {
    dispatch(fetchStates({}));
  }, [dispatch]);

  useEffect(() => {
    if (editUuid) {
      dispatch(fetchDistrictById(editUuid));
    }
  }, [editUuid, dispatch]);

  useEffect(() => {
    if (editUuid && district && districtFetchStatus === "success") {
      reset({
        state_uuid: district.state?.uuid || "",
        name: district.name || "",
        status: Boolean(district.status),
      });
    }
  }, [district, districtFetchStatus, editUuid, reset]);

  const onSubmit = async (formData) => {
    try {
      const payload = {
        state_uuid: formData.state_uuid,
        name: formData.name,
        status: formData.status,
      };

      if (editUuid) {
        await dispatch(updateDistrict({ uuid: editUuid, data: payload })).unwrap();
        showApiSuccess("District updated successfully");
      } else {
        await dispatch(createDistrict(payload)).unwrap();
        showApiSuccess("District created successfully");
      }

      navigate("/districts");
    } catch (error) {
      showApiError(null, error || `Failed to ${editUuid ? "update" : "create"} district`);
    }
  };

  useEffect(() => {
    return () => {
      dispatch(resetDistrictStatuses());
    };
  }, [dispatch]);

  const isSubmitting =
    districtCreateStatus === "loading" || districtUpdateStatus === "loading";

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {loading && <Loader />}

      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate("/districts")}
              className="text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="h-6 w-6" />
            </button>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {editUuid ? "Edit District" : "Add New District"}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {editUuid
                  ? "Update district information"
                  : "Create a new district linked to a state"}
              </p>
            </div>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            District Information
          </h3>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                State <span className="text-red-500">*</span>
              </label>
              <select
                {...register("state_uuid")}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select a state</option>
                {states.map((state) => (
                  <option key={state.uuid} value={state.uuid}>
                    {state.name}
                  </option>
                ))}
              </select>
              {errors?.state_uuid && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.state_uuid.message}
                </p>
              )}
            </div>

            <div>
              <TextField
                label="District Name"
                required
                placeholder="Enter district name"
                {...register("name")}
                error={errors?.name?.message}
              />
            </div>

            <div>
              <Controller
                control={control}
                name="status"
                render={({ field }) => (
                  <CheckboxField
                    label="Active Status"
                    checked={Boolean(field.value)}
                    onChange={(checked) => field.onChange(checked)}
                  />
                )}
              />
              <p className="text-sm text-gray-500 mt-1">
                Enable this district to make it available for selection
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate("/districts")}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="h-4 w-4" />
              <span>
                {isSubmitting
                  ? "Saving..."
                  : editUuid
                  ? "Update District"
                  : "Create District"}
              </span>
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
